import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { getMaxUploadSizeByChannel } from '@chatwoot/utils';
import { DirectUpload } from 'activestorage';
import {
  DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export default {
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    installationLimit() {
      return resolveMaximumFileUploadSize(
        this.globalConfig.maximumFileUploadSize
      );
    },
  },

  methods: {
    maxSizeFor(mime) {
      // Use default/installation limit for private notes
      if (this.isOnPrivateNote) {
        return this.installationLimit;
      }

      // If it's a video, ALWAYS allow up to 150MB because our backend compresses it!
      if (mime && mime.startsWith('video/')) {
        return 150;
      }

      const channelType = this.inbox?.channel_type;

      if (!channelType || channelType === INBOX_TYPES.WEB) {
        return this.installationLimit;
      }

      const channelLimit = getMaxUploadSizeByChannel({
        channelType,
        medium: this.inbox?.medium, // e.g. 'sms' | 'whatsapp'
        mime, // e.g. 'image/png'
      });

      if (channelLimit === DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE) {
        return this.installationLimit;
      }

      return Math.min(channelLimit, this.installationLimit);
    },
    alertOverLimit(maxSizeMB) {
      useAlert(
        this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
          MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maxSizeMB,
        })
      );
    },
    async onFileUpload(file) {
      if (!file) return;
      
      let processedFile = file;
      const actualFile = file.file || file;

      try {
        // --- VIDEO handling: skip conversion and rely on backend FFMPEG ---
        if (actualFile.type && actualFile.type.startsWith('video/')) {
          // Check if video is under 150MB to allow backend compression
          const sizeMB = (actualFile.size / (1024 * 1024)).toFixed(2);
          if (actualFile.size > 150 * 1024 * 1024) {
            useAlert(
              `⚠️ Video is too large (${sizeMB} MB). Maximum allowed size is 150 MB. The server will compress videos under 150MB automatically.`
            );
            return; // Don't upload
          }

          // Send original file as-is, backend will run FFMPEG to compress it to under 16MB
          processedFile = file;
        }
        // --- IMAGE handling: compress to JPEG ---
        else if (!actualFile.isRecordedAudio) {
          const { compressImage } = await import('dashboard/helper/imageCompressor');
          const compressed = await compressImage(actualFile);
          
          if (file.file) {
            processedFile = { ...file, file: compressed };
          } else {
            processedFile = compressed;
          }
        }
      } catch (e) {
        console.error("File processing failed, falling back to original", e);
      }

      if (this.globalConfig.directUploadsEnabled) {
        this.onDirectFileUpload(processedFile);
      } else {
        this.onIndirectFileUpload(processedFile);
      }
    },

    onDirectFileUpload(file) {
      if (!file) return;

      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return;
      }

      const upload = new DirectUpload(
        file.file,
        `/api/v1/accounts/${this.accountId}/conversations/${this.currentChat.id}/direct_uploads`,
        {
          directUploadWillCreateBlobWithXHR: xhr => {
            xhr.setRequestHeader(
              'api_access_token',
              this.currentUser.access_token
            );
          },
        }
      );

      upload.create((error, blob) => {
        if (error) {
          useAlert(error);
        } else {
          this.attachFile({ file, blob });
        }
      });
    },

    onIndirectFileUpload(file) {
      if (!file) return;

      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return;
      }

      this.attachFile({ file });
    },
  },
};
